/**
 * Chilean peso denominations used for cash drawer counting.
 * Ordered high → low for cashier-friendly entry.
 */
export const DENOMINACIONES_CLP = [
    { valor: 20000, tipo: 'billete', label: '$20.000' },
    { valor: 10000, tipo: 'billete', label: '$10.000' },
    { valor: 5000, tipo: 'billete', label: '$5.000' },
    { valor: 2000, tipo: 'billete', label: '$2.000' },
    { valor: 1000, tipo: 'billete', label: '$1.000' },
    { valor: 500, tipo: 'moneda', label: '$500' },
    { valor: 100, tipo: 'moneda', label: '$100' },
    { valor: 50, tipo: 'moneda', label: '$50' },
    { valor: 10, tipo: 'moneda', label: '$10' },
];

/** @returns {Record<string, number>} quantity map keyed by denomination value */
export function createEmptyDesglose() {
    return DENOMINACIONES_CLP.reduce((acc, d) => {
        acc[String(d.valor)] = 0;
        return acc;
    }, {});
}

/** Normalize arbitrary desglose object into known keys with non-negative integers. */
export function normalizeDesglose(raw) {
    const empty = createEmptyDesglose();
    if (!raw || typeof raw !== 'object') return empty;
    for (const d of DENOMINACIONES_CLP) {
        const key = String(d.valor);
        const n = parseInt(raw[key] ?? raw[d.valor], 10);
        empty[key] = Number.isFinite(n) && n > 0 ? n : 0;
    }
    return empty;
}

/** Total CLP from quantity map. */
export function sumDesglose(desglose) {
    const map = normalizeDesglose(desglose);
    return DENOMINACIONES_CLP.reduce((sum, d) => sum + d.valor * (map[String(d.valor)] || 0), 0);
}

/** Compact payload: only denominations with quantity > 0. */
export function compactDesglose(desglose) {
    const map = normalizeDesglose(desglose);
    const out = {};
    for (const d of DENOMINACIONES_CLP) {
        const q = map[String(d.valor)] || 0;
        if (q > 0) out[String(d.valor)] = q;
    }
    return out;
}

export function formatClp(amount) {
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0,
    }).format(amount || 0);
}

/** Parse CLP amount from input (allows thousand dots). */
export function parseMontoClp(raw) {
    if (raw === '' || raw == null) return null;
    const n = parseFloat(String(raw).replace(/\./g, '').replace(/,/g, '.'));
    return Number.isFinite(n) ? n : null;
}

/** Human-readable bill/coin breakdown, e.g. "11×$10.000, 2×$5.000". */
export function formatDesgloseDetalle(desglose) {
    const map = normalizeDesglose(desglose);
    const parts = [];
    for (const d of DENOMINACIONES_CLP) {
        const q = map[String(d.valor)] || 0;
        if (q > 0) parts.push(`${q}×${d.label}`);
    }
    return parts.join(', ');
}

/** Rows for print: qty × denomination = subtotal. */
export function desgloseLineas(desglose) {
    const map = normalizeDesglose(desglose);
    const lines = [];
    for (const d of DENOMINACIONES_CLP) {
        const qty = map[String(d.valor)] || 0;
        if (qty > 0) {
            lines.push({
                qty,
                valor: d.valor,
                label: d.label,
                subtotal: qty * d.valor,
            });
        }
    }
    return lines;
}

/** True when at least one denomination quantity was entered. */
export function hasDesgloseDetalle(desglose) {
    return Object.keys(compactDesglose(desglose)).length > 0;
}

/**
 * If detail was entered, it must equal the declared amount.
 * @returns {string|null} error message or null if ok
 */
export function validateDesgloseVsMonto(desglose, monto) {
    if (!hasDesgloseDetalle(desglose)) return null;
    const sum = sumDesglose(desglose);
    if (Math.abs(sum - Number(monto || 0)) > 0) {
        return `El detalle de billetes/monedas (${formatClp(sum)}) no coincide con el monto (${formatClp(monto)}).`;
    }
    return null;
}
